import { useCallback, useEffect, useState } from 'react';
import { describeError } from '../../services/errorMessage';
import { fetchMitarbeiter, fetchTeilnahmen, fetchUnterweisungen } from '../../services/sifa/sifaService';
import { SifaMitarbeiter, SifaTeilnahme, SifaUnterweisung } from '../../services/sifa/types';
import { sortiereTeilnahmen, sortiereUnterweisungen } from './unterweisungModulLogic';

/**
 * Das Laden der Unterweisungen samt Teilnahmen und Mitarbeiterstamm — der
 * einzige Zustand, den der Container haelt.
 *
 * Alle drei Listen in EINEM Hook: die Ablage zeigt je Kurs die Teilnahmen,
 * die Teilnahmenliste braucht den Stamm fuer „Zuordnen". Ein Hook je Liste
 * hiesse drei Ladezustaende und drei Fehlerzeilen fuer eine Ansicht.
 *
 * Die Entscheidungen (Sortierung, Zaehlen, Filtern) stehen als pure
 * Funktionen in unterweisungModulLogic.ts und sind ohne DOM pruefbar.
 */
export const useUnterweisungen = (projectId: string | null | undefined) => {
  const [unterweisungen, setUnterweisungen] = useState<SifaUnterweisung[]>([]);
  const [teilnahmen, setTeilnahmen] = useState<SifaTeilnahme[]>([]);
  const [mitarbeiter, setMitarbeiter] = useState<SifaMitarbeiter[]>([]);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState<string | null>(null);

  const neuLaden = useCallback(async () => {
    if (!projectId) {
      setUnterweisungen([]);
      setTeilnahmen([]);
      setMitarbeiter([]);
      setLaden(false);
      return;
    }
    setLaden(true);
    setFehler(null);
    try {
      const [u, t, m] = await Promise.all([
        fetchUnterweisungen(projectId),
        fetchTeilnahmen({ projectId }),
        fetchMitarbeiter(projectId),
      ]);
      setUnterweisungen(sortiereUnterweisungen(u));
      setTeilnahmen(sortiereTeilnahmen(t));
      setMitarbeiter(m.filter((x) => !x.zusammengefuehrtIn));
    } catch (e) {
      setFehler(describeError(e));
    } finally {
      setLaden(false);
    }
  }, [projectId]);

  useEffect(() => { void neuLaden(); }, [neuLaden]);

  /**
   * Nimmt eine geaenderte Unterweisung in die Liste auf — vorhandene wird
   * ersetzt, neue eingereiht. Kein Neuladen nach jedem Speichern: die
   * Antwort des Servers steht schon im Ergebnis des Schreibens.
   */
  const uebernehmen = useCallback((geaendert: SifaUnterweisung) => {
    setUnterweisungen((prev) => sortiereUnterweisungen([...prev.filter((u) => u.id !== geaendert.id), geaendert]));
  }, []);

  const uebernehmeTeilnahme = useCallback((geaendert: SifaTeilnahme) => {
    setTeilnahmen((prev) => sortiereTeilnahmen([...prev.filter((t) => t.id !== geaendert.id), geaendert]));
  }, []);

  /** Fuer Massenaenderungen (Buchung): die Liste als Ganzes ersetzen. */
  const setzeTeilnahmen = useCallback((aendere: (prev: SifaTeilnahme[]) => SifaTeilnahme[]) => {
    setTeilnahmen((prev) => sortiereTeilnahmen(aendere(prev)));
  }, []);

  const uebernehmeMitarbeiter = useCallback((geaendert: SifaMitarbeiter) => {
    setMitarbeiter((prev) => [...prev.filter((m) => m.id !== geaendert.id), geaendert]);
  }, []);

  return {
    unterweisungen, teilnahmen, mitarbeiter, laden, fehler,
    neuLaden, uebernehmen, uebernehmeTeilnahme, setzeTeilnahmen, uebernehmeMitarbeiter,
  };
};
